import React from 'react';

import {
	ChevronDownIcon,
	ChevronUpDownIcon,
	ChevronUpIcon,
} from '@heroicons/react/24/outline';

import { List } from './List';
import { Text } from './Text';
import { Row } from './Flex';

import { cn } from 'domain/core';
import { useA11y } from 'domain/hooks';

export type ListHeader = {
	label: React.ReactNode;
	code: string;
	sortable: boolean;
	col: number;
	className?: string;
};

export enum SortDir {
	Asc = 'ASC',
	Desc = 'DESC',
}

export interface SortInfo {
	sortField: string;
	sortDir: SortDir;
}

type ListElementRenderer = (
	field: ListHeader,
	element: any,
	index: number
) => React.ReactNode;

type HeaderRenderer = (field: ListHeader, index: number) => React.ReactNode;

type RowClickCallback = (element: any, index: number) => void;

const DataListContext = React.createContext<{
	fields: ListHeader[];
}>({
	fields: [],
});

const useDataListContext = () => React.useContext(DataListContext);

const DataListProvider = React.memo(
	({
		fields,
		children,
	}: React.PropsWithChildren<Pick<DataListProps, 'fields'>>) => {
		const ctx = React.useMemo(() => ({ fields: fields ?? [] }), [fields]);

		return (
			<DataListContext.Provider value={ctx}>
				{children}
			</DataListContext.Provider>
		);
	}
);

interface DataListProps extends React.PropsWithChildren {
	fields?: ListHeader[];
	className?: string;
}
/**
 * List component that build itself based on fields and data
 */
const DataList = (props: DataListProps) => {
	return (
		<DataListProvider fields={props.fields}>
			<List>
				<List.Wrapper>{props.children}</List.Wrapper>
			</List>
		</DataListProvider>
	);
};

interface DataListHeaderProps {
	sort?: SortInfo;
	className?: string;
	renderer?: HeaderRenderer;
	onSortChange?: (sort: SortInfo) => void;
}
const DataListHeader = (props: DataListHeaderProps) => {
	const { fields } = useDataListContext();

	const columns = useColumnStyles(fields);

	return (
		<List.RowGroup className='h-auto w-full'>
			<List.Row
				style={{
					gridTemplateColumns: columns,
				}}
				className={cn(
					'data-list-header',
					'grid hover:bg-neutral-100',
					props.className
				)}>
				{fields.map((field, index) => {
					if (props.renderer) {
						return props.renderer(field, index);
					}

					return (
						<DefaultHeaderRender
							key={field.code}
							field={field}
							onSortChange={props.onSortChange}
							sort={props.sort}
						/>
					);
				})}
			</List.Row>
		</List.RowGroup>
	);
};

const SortIcon = ({
	field,
	sort,
}: Pick<DefaultHeaderRenderProps, 'field' | 'sort'>) => {
	if (field.sortable) {
		const iconClassName = cn(
			'w-6 h-6 text-neutral-500 group-hover:text-neutral-800'
		);
		if (sort && sort.sortField === field.code) {
			return sort.sortDir === SortDir.Asc ? (
				<ChevronUpIcon className={iconClassName} />
			) : (
				<ChevronDownIcon className={iconClassName} />
			);
		}

		return <ChevronUpDownIcon className={iconClassName} />;
	}
	return null;
};

interface DefaultHeaderRenderProps
	extends Pick<DataListHeaderProps, 'sort' | 'onSortChange'> {
	field: ListHeader;
}
const DefaultHeaderRender = (props: DefaultHeaderRenderProps) => {
	const [title, ariaLabel] = useA11y({
		children: props.field.label,
		ariaLabelPrefix: 'List header',
	});

	const changeSort = React.useCallback(() => {
		if (props.onSortChange && props.field.sortable) {
			if (props.sort && props.sort.sortField === props.field.code) {
				props.onSortChange({
					sortDir:
						props.sort.sortDir === SortDir.Asc ? SortDir.Desc : SortDir.Asc,
					sortField: props.field.code,
				});
			} else {
				props.onSortChange({
					sortDir: SortDir.Asc,
					sortField: props.field.code,
				});
			}
		}
	}, [props.onSortChange, props.field, props.sort]);

	return (
		<List.Header
			title={title}
			aria-label={ariaLabel}
			style={{
				gridColumn: `span ${props.field.col}`,
			}}
			className={cn(
				'data-list-default-header',
				props.field.sortable && 'cursor-pointer',
				'group'
			)}
			onClick={changeSort}>
			<Row className='w-full !justify-start'>
				<Text
					className={cn(
						props.field.sortable && 'group-hover:text-neutral-800'
					)}>
					{props.field.label}
				</Text>
				<SortIcon field={props.field} sort={props.sort} />
			</Row>
		</List.Header>
	);
};

interface DataListBodyProps<T extends object = {}> {
	data: T[];
	onRowClick?: RowClickCallback;
	renderer?: ListElementRenderer;
}
const DataListBody = (props: DataListBodyProps) => {
	const { fields } = useDataListContext();
	const columns = useColumnStyles(fields);

	const onClick = (element: any, index: number) => {
		if (props.onRowClick) {
			props.onRowClick(element, index);
		}
	};

	return (
		<List.RowGroup>
			{props.data.map((entry, index) => (
				<List.Row
					style={{
						gridTemplateColumns: columns,
					}}
					key={index}
					className={cn(
						'data-list-row',
						'grid',
						props.onRowClick && 'cursor-pointer hover:bg-neutral-200'
					)}
					onClick={() => onClick(entry, index)}>
					{fields.map((field, fieldIndex) => {
						if (props.renderer) {
							return props.renderer(field, entry, index);
						}
						return <DefaultCell key={fieldIndex} entry={entry} field={field} />;
					})}
				</List.Row>
			))}
		</List.RowGroup>
	);
};

interface DefaultCellProps {
	entry: any;
	field: ListHeader;
}
const DefaultCell = (props: DefaultCellProps) => {
	const value = props.entry[props.field.code];

	const [title, ariaLabel] = useA11y({ children: value });
	return (
		<List.Cell
			style={{
				gridColumn: `span ${props.field.col}`,
			}}
			title={title}
			aria-label={ariaLabel}
			className={cn('data-list-default-cell', '!justify-start')}>
			<Text>{value}</Text>
		</List.Cell>
	);
};

const useColumnStyles = (fields: ListHeader[]) =>
	React.useMemo(() => {
		const total = fields.reduce((acc: number, curr: ListHeader) => {
			return acc + curr.col;
		}, 0);

		return `repeat(${total}, minmax(0, 1fr))`;
	}, [fields]);

DataList.displayName = 'DataList';
/**
 * Header component. It manages sorting and header placement
 */
DataList.Headers = DataListHeader;
/**
 * List body to manage rows. Allows to render custom cells, selection, etc...
 */
DataList.Body = DataListBody;

export { DataList };
